/**
 * useClaimPDF — fetch a filled CMS-1500 PDF for a claim or note.
 *
 * Race-guarded (latestRequestRef) so a slow fetch landing after the
 * user navigates to a different claim doesn't overwrite the new
 * claim's PDF state.  Same defensive pattern as useClientEligibility.
 *
 * The fetched PDF is exposed as a blob URL so the consumer can
 * embed it in an <iframe> and/or trigger a download without
 * round-tripping through the API a second time.
 *
 * Lifecycle:
 *   const { fetchClaimPdf, fetchNotePreviewPdf, pdfUrl, loading,
 *           error, downloadAs, reset } = useClaimPDF();
 *
 *   await fetchClaimPdf(claimId);           // sets pdfUrl
 *   <iframe src={pdfUrl} />                 // embed
 *   downloadAs('cms1500_C-100.pdf');         // trigger download
 *   reset();                                 // free memory + state
 *
 * The hook automatically revokes the blob URL on unmount so we
 * don't leak memory across many opens of the dialog.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export function useClaimPDF() {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filename, setFilename] = useState(null);

  // Track the latest request the caller asked for so a slower
  // earlier request can't overwrite a newer result.
  const latestRequestKeyRef = useRef(null);
  // Hold the most recently created blob URL so we can revoke it
  // before issuing a new one (memory hygiene).
  const blobUrlRef = useRef(null);

  const _setBlob = (bytes, suggestedFilename) => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
    }
    const blob = new Blob([bytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    blobUrlRef.current = url;
    setPdfUrl(url);
    setFilename(suggestedFilename || null);
  };

  const fetchClaimPdf = useCallback(async (claimId) => {
    if (!claimId) return;
    const key = `claim:${claimId}`;
    latestRequestKeyRef.current = key;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/claims/${claimId}/cms1500-pdf`, {
        responseType: 'arraybuffer',
      });
      // Race guard: drop the response if the caller has moved on.
      if (latestRequestKeyRef.current !== key) return;
      _setBlob(res.data, `cms1500_${claimId}.pdf`);
    } catch (e) {
      if (latestRequestKeyRef.current !== key) return;
      setError(e?.response?.data?.detail || e.message || 'Failed to load PDF');
    } finally {
      if (latestRequestKeyRef.current === key) setLoading(false);
    }
  }, []);

  const fetchNotePreviewPdf = useCallback(async (noteId) => {
    if (!noteId) return;
    const key = `note:${noteId}`;
    latestRequestKeyRef.current = key;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(
        `${API}/claims/preview-from-note/${noteId}/cms1500-pdf`,
        {},
        { responseType: 'arraybuffer' }
      );
      if (latestRequestKeyRef.current !== key) return;
      _setBlob(res.data, `cms1500_preview_${noteId}.pdf`);
    } catch (e) {
      if (latestRequestKeyRef.current !== key) return;
      setError(e?.response?.data?.detail || e.message || 'Failed to load PDF');
    } finally {
      if (latestRequestKeyRef.current === key) setLoading(false);
    }
  }, []);

  /** Trigger a browser download of the currently-loaded PDF. */
  const downloadAs = useCallback((suggestedName) => {
    if (!blobUrlRef.current) return;
    const a = document.createElement('a');
    a.href = blobUrlRef.current;
    a.download = suggestedName || filename || 'cms1500.pdf';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, [filename]);

  const reset = useCallback(() => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
    setPdfUrl(null);
    setError(null);
    setFilename(null);
    latestRequestKeyRef.current = null;
  }, []);

  // Free the blob URL on unmount — prevents leaks when the user
  // closes the dialog without explicitly resetting.
  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
      }
    };
  }, []);

  return {
    fetchClaimPdf,
    fetchNotePreviewPdf,
    pdfUrl,
    filename,
    loading,
    error,
    downloadAs,
    reset,
  };
}
